import React, { useEffect, useRef } from "react";

import Logger from "../../lib/logger";
import { beepDrop } from "../../lib/scanFeedback";

interface HidScannerInputProps {
  onScan: (code: string) => void;
  cooldownMs?: number;
  minLength?: number;
  visible?: boolean;
  validate?: (code: string) => boolean;
  logTag?: string;
}

const visibleStyle: React.CSSProperties = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "40rem",
  padding: "2rem 2.5rem",
  fontSize: "2.4rem",
  textAlign: "center",
  borderRadius: "1rem",
  border: "2px solid black",
  color: "black",
  outline: "none",
  zIndex: 100,
};

const hiddenStyle: React.CSSProperties = {
  position: "fixed",
  top: "-9999px",
  left: "-9999px",
  width: "1px",
  height: "1px",
  opacity: 0,
};

const HidScannerInput: React.FC<HidScannerInputProps> = ({
  onScan,
  cooldownMs = 1500,
  minLength = 4,
  visible = false,
  validate,
  logTag = "hid-scanner",
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const lastScanAtRef = useRef(0);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <input
      ref={inputRef}
      type="text"
      autoComplete="off"
      onBlur={(e) => e.target.focus()}
      onKeyDown={(e) => {
        if (e.key !== "Enter" && e.key !== "Tab") {
          return;
        }

        const code = e.currentTarget.value.trim();

        if (code.length < minLength) {
          return;
        }

        e.preventDefault();

        const now = Date.now();
        const sinceLast = now - lastScanAtRef.current;

        if (sinceLast < cooldownMs) {
          Logger.warn(`[${logTag}] scan dropped (cooldown)`, {
            code,
            sinceLastMs: sinceLast,
          });
          beepDrop();
          e.currentTarget.value = "";
          return;
        }

        if (validate && !validate(code)) {
          Logger.warn(`[${logTag}] scan dropped (invalid)`, { code });
          beepDrop();
          e.currentTarget.value = "";
          return;
        }

        lastScanAtRef.current = now;

        Logger.info(`[${logTag}] scan accepted`, { code });
        onScan(code);
        e.currentTarget.value = "";
      }}
      style={visible ? visibleStyle : hiddenStyle}
      aria-hidden={!visible}
    />
  );
};

export default HidScannerInput;
